/* Aaria London: the chosen variant's photo owns the hero slot.
 *
 * Desktop only. Everything this does is two attributes and two classes,
 * and every rule that reads them lives inside @media (min-width: 1000px)
 * in layout/theme.liquid. Nothing is moved, nothing is rewritten, so the
 * mobile carousel is left exactly as Prestige built it.
 *
 * That is deliberate. The first version moved the chosen photo to the
 * front of the carousel, because aaria.css picks the hero by DOM order:
 * the first .product-gallery__media:not([hidden]) gets column one and
 * position sticky, later siblings get column two. Moving the node worked
 * on desktop, but the new order survived into the mobile layout for the
 * rest of the session, so a page loaded wide and then narrowed showed a
 * shuffled carousel. A class the CSS can target has no such reach.
 *
 * Three jobs:
 *
 * 1. Tag. Every photo that belongs to a variant gets
 *    data-aaria-variant-shot, so the CSS can keep packshots out of
 *    column two and leave that column to the lifestyle photography.
 *    Done here rather than in Liquid because product-gallery.liquid is
 *    a Prestige file.
 *
 * 2. Crown. The chosen variant's photo gets .aaria-hero, which the CSS
 *    promotes into the sticky column one slot. It also loses
 *    data-aaria-more, so the "View all photos" cap in aaria.css can
 *    never hide the very thing that was just selected.
 *
 * 3. Flag the carousel with .aaria-has-hero. This is what lets the CSS
 *    push the natural first photo into column two without :has(). With
 *    :has() a browser that did not support it would drop the rule and
 *    leave two photos sharing the sticky column one slot, overlapping.
 *    A plain class cannot fail that way.
 *
 * Loaded with defer on product pages from layout/theme.liquid, after
 * aaria-ui.js, so the cap has already been applied by the time this runs.
 */
(function () {
  var DESKTOP = '(min-width: 1000px)';

  function carousel() {
    return document.querySelector(
      '.shopify-section--main-product .product-gallery__carousel'
    );
  }

  function productHandle() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  // variant id -> media id. Built once from /products/handle.js, because
  // the variant to media mapping is not in the page markup.
  var mediaByVariant = null;
  var inFlight = null;

  function loadMap() {
    if (mediaByVariant) return Promise.resolve(mediaByVariant);
    if (inFlight) return inFlight;

    var handle = productHandle();
    if (!handle) return Promise.resolve(null);

    inFlight = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin'
    })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (product) {
        if (!product || !product.variants) return null;

        var map = {};
        product.variants.forEach(function (variant) {
          if (variant.featured_media && variant.featured_media.id) {
            map[String(variant.id)] = String(variant.featured_media.id);
          }
        });

        mediaByVariant = map;
        return map;
      })
      .catch(function () { return null; });

    return inFlight;
  }

  // Prestige writes the chosen variant into the form's hidden id input,
  // so read that rather than trying to work the variant out from
  // whichever swatch or select was touched.
  function selectedVariantId() {
    var input = document.querySelector(
      '.shopify-section--main-product form[action*="/cart/add"] [name="id"]'
    );
    return input && input.value ? String(input.value) : null;
  }

  function tagVariantShots(map) {
    var car = carousel();
    if (!car || car.dataset.aariaVariantShotsTagged) return;

    var isVariantShot = {};
    Object.keys(map).forEach(function (variantId) {
      isVariantShot[map[variantId]] = true;
    });

    var tagged = 0;
    [].slice.call(car.querySelectorAll('.product-gallery__media'))
      .forEach(function (media) {
        if (isVariantShot[media.dataset.mediaId]) {
          media.setAttribute('data-aaria-variant-shot', '');
          tagged++;
        }
      });

    // Only latch once something was actually tagged, so a fetch that came
    // back empty does not block a later attempt.
    if (tagged) car.dataset.aariaVariantShotsTagged = '1';
  }

  function uncrown(car) {
    if (!car) return;
    car.classList.remove('aaria-has-hero');
    [].slice.call(car.querySelectorAll('.aaria-hero'))
      .forEach(function (el) { el.classList.remove('aaria-hero'); });
  }

  function crown(mediaId) {
    var car = carousel();
    if (!car || !mediaId) return;

    var media = car.querySelector(
      '.product-gallery__media[data-media-id="' + mediaId + '"]'
    );

    // A variant with no photo of its own leaves the hero as it is, which
    // is better than blanking it or dropping back to the product default.
    if (!media) return;

    uncrown(car);

    // The chosen photo must never be one of the shots the cap hides
    // behind "View all photos".
    media.removeAttribute('data-aaria-more');
    media.classList.add('aaria-hero');
    car.classList.add('aaria-has-hero');
  }

  function sync() {
    if (!window.matchMedia(DESKTOP).matches) return;

    var variantId = selectedVariantId();
    if (!variantId) return;

    loadMap().then(function (map) {
      if (!map) return;
      tagVariantShots(map);
      crown(map[variantId]);
    });
  }

  function start() {
    // Covers landing on the page with ?variant= already pointing at a
    // single ear shot.
    sync();

    // Delegated, so it survives Prestige re-rendering the picker. The
    // timeout lets Prestige update the hidden input first.
    document.addEventListener('change', function (event) {
      if (!event.target || !event.target.closest) return;
      if (!event.target.closest('.shopify-section--main-product')) return;
      window.setTimeout(sync, 0);
    });

    // Crossing the breakpoint, which in practice means the theme
    // editor's device toggle or a rotated tablet. Narrow takes the crown
    // off so mobile is untouched; wide puts it back.
    var wide = window.matchMedia(DESKTOP);
    var onBreakpoint = function () {
      if (!wide.matches) uncrown(carousel());
      sync();
    };

    if (wide.addEventListener) wide.addEventListener('change', onBreakpoint);
    else if (wide.addListener) wide.addListener(onBreakpoint);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Belt and braces on ordering: if this ever executes before the cap
  // has been applied, load fires afterwards and puts it right.
  window.addEventListener('load', sync);
})();
